/**
 * PDF re-composer — builds a NEW PDF that embeds selected pages from
 * the source PDF onto the user's chosen paper size, orientation, margin
 * and scale.
 *
 * Why not just rasterise? Because that produces bitmap text — fuzzy in
 * print and not selectable. Embedding via pdf-lib keeps the original
 * vector content (text, paths) intact, so the printed output is exactly
 * as crisp as the source PDF.
 */

import { PDFDocument } from "pdf-lib";
import { type MarginPreset, marginCss, type Orientation, type PaperSize } from "./print-types.js";

/** Paper sizes in PDF points (1pt = 1/72 inch), portrait. */
const PAPER_PT: Record<PaperSize, [number, number]> = {
  A3: [841.89, 1190.55],
  A4: [595.28, 841.89],
  A5: [419.53, 595.28],
  Letter: [612, 792],
  Legal: [612, 1008],
};

/** Convert "8mm" / "15mm" / "25mm" / "0" → PDF points. */
function marginPoints(m: MarginPreset): number {
  const css = marginCss(m); // e.g. "15mm" or "0"
  if (css === "0") return 0;
  const mm = Number.parseFloat(css);
  return (mm / 25.4) * 72;
}

export interface RecomposeOptions {
  srcBytes: ArrayBuffer | Uint8Array;
  /** 1-based page numbers to include. */
  pages: number[];
  paper: PaperSize;
  orientation: Orientation;
  margin: MarginPreset;
  /** 25..400 — applied on top of fit-to-page. 100 = exact fit. */
  scalePct: number;
}

/**
 * Returns the bytes of the new PDF, ready to wrap in a Blob.
 *
 * Throws if the source bytes aren't a valid PDF or no pages match.
 */
export async function recomposePdf(opts: RecomposeOptions): Promise<Uint8Array> {
  const src = await PDFDocument.load(opts.srcBytes, { ignoreEncryption: true });
  const total = src.getPageCount();
  // 0-based indices, clamped + deduplicated.
  const indices = Array.from(
    new Set(opts.pages.filter((p) => p >= 1 && p <= total).map((p) => p - 1)),
  );
  if (indices.length === 0) {
    throw new Error("Keine gültigen Seiten zum Drucken ausgewählt.");
  }

  const dest = await PDFDocument.create();
  const embedded = await dest.embedPdf(src, indices);

  const [portraitW, portraitH] = PAPER_PT[opts.paper];
  const [pageW, pageH] =
    opts.orientation === "landscape" ? [portraitH, portraitW] : [portraitW, portraitH];
  const margin = marginPoints(opts.margin);
  const availW = Math.max(1, pageW - 2 * margin);
  const availH = Math.max(1, pageH - 2 * margin);
  const userScale = Math.max(0.1, Math.min(4, opts.scalePct / 100));

  for (const ep of embedded) {
    const newPage = dest.addPage([pageW, pageH]);
    // Fit-to-page first (preserves aspect ratio), then user scale on top.
    const fitScale = Math.min(availW / ep.width, availH / ep.height);
    const finalScale = fitScale * userScale;
    const drawW = ep.width * finalScale;
    const drawH = ep.height * finalScale;
    const x = (pageW - drawW) / 2;
    const y = (pageH - drawH) / 2;
    newPage.drawPage(ep, { x, y, width: drawW, height: drawH });
  }

  return await dest.save();
}

/**
 * Fetches a PDF URL (blob: or signed HTTP URL) into a buffer suitable
 * for `recomposePdf`. Centralised here so callers don't have to repeat
 * the fetch + arrayBuffer dance.
 */
export async function fetchPdfBytes(src: string): Promise<ArrayBuffer> {
  const res = await fetch(src);
  if (!res.ok) {
    throw new Error(`PDF konnte nicht geladen werden (HTTP ${res.status}).`);
  }
  return await res.arrayBuffer();
}
